import { useEffect, useMemo, useRef, useState } from "react";

import type {
  AppliedFramingReceipt,
  FramingBox,
  FramingCoordinateSpace,
  FramingDecision,
  FramingDeliveredSubjectEvidence,
  FramingEvidenceMode,
  FramingObservation,
} from "./framingInspector";
import {
  containedMediaRect,
  cropWindowAtTime,
  displayDimensionsForSpace,
  observationsAtTime,
  projectNormalizedBox,
} from "./framingOverlayMath";
import {
  deliveredEvidenceAtTime,
  framingOverlayEvidencePolicy,
} from "./framingViewUtils";

export type FramingOverlayLayer =
  | "observations"
  | "delivered_semantic"
  | "protected"
  | "source_transform"
  | "visible_content"
  | "planned_crop"
  | "safe_zone"
  | "applied_crop"
  | "effect_viewport";

type OverlayRect = {
  key: string;
  box: FramingBox;
  label: string;
  color: string;
  dashed?: boolean;
};

export function FramingOverlay({
  coordinateSpace,
  coordinateSpaces,
  observations,
  deliveredEvidence = [],
  evidenceMode,
  decision,
  receipt,
  timeSeconds,
  visibleLayers,
}: {
  coordinateSpace: FramingCoordinateSpace | null;
  coordinateSpaces: FramingCoordinateSpace[];
  observations: FramingObservation[];
  deliveredEvidence?: FramingDeliveredSubjectEvidence[];
  evidenceMode: FramingEvidenceMode;
  decision: FramingDecision | null;
  receipt: AppliedFramingReceipt | null;
  timeSeconds: number;
  visibleLayers: ReadonlySet<FramingOverlayLayer>;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const update = () => {
      const bounds = svg.getBoundingClientRect();
      setSize((current) =>
        current.width === bounds.width && current.height === bounds.height
          ? current
          : { width: bounds.width, height: bounds.height },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(svg);
    return () => observer.disconnect();
  }, []);

  const displayDimensions = displayDimensionsForSpace(
    coordinateSpace,
    coordinateSpaces,
  );
  const mediaWidth = displayDimensions?.width ?? receipt?.sourceWidth ?? 1;
  const mediaHeight = displayDimensions?.height ?? receipt?.sourceHeight ?? 1;
  const mediaRect = containedMediaRect(
    size.width,
    size.height,
    mediaWidth,
    mediaHeight,
  );

  const rects = useMemo(() => {
    const overlays: OverlayRect[] = [];
    const spaceId = coordinateSpace?.id ?? null;
    const normalizedStage = coordinateSpace?.stage
      .toLowerCase()
      .replaceAll("-", "_");
    const evidencePolicy = framingOverlayEvidencePolicy(
      evidenceMode,
      coordinateSpace?.evidenceRole,
    );
    const planEvidenceAllowed = evidencePolicy.plan;
    const appliedEvidenceAllowed = evidencePolicy.applied;
    const spaceObservations = observationsAtTime(
      observations.filter(
        (observation) => observation.coordinateSpaceId === spaceId,
      ),
      timeSeconds,
    );
    if (visibleLayers.has("observations")) {
      spaceObservations.forEach((observation, index) => {
        overlays.push({
          key: `observation-${observation.subjectId ?? "unassigned"}-${observation.frameIndex ?? "no-frame"}-${observation.timestampSeconds ?? "timeless"}-${index}`,
          box: observation.bbox,
          label: [
            observation.label ?? observation.kind ?? "subject",
            observation.detector,
            observation.confidence == null
              ? null
              : `${Math.round(observation.confidence * 100)}%`,
            observation.timestampSeconds == null
              ? null
              : `@ ${observation.timestampSeconds.toFixed(2)}s`,
          ]
            .filter(Boolean)
            .join(" · "),
          color: "#22d3ee",
        });
      });
    }
    if (visibleLayers.has("delivered_semantic")) {
      deliveredEvidenceAtTime(deliveredEvidence, spaceId, timeSeconds).forEach(
        (evidence, index) => {
          overlays.push({
            key: `delivered-semantic-${evidence.subjectId ?? index}-${evidence.sampleIndex ?? index}`,
            box: evidence.bbox,
            label: [
              `delivered ${evidence.kind ?? "subject"}`,
              evidence.matchStatus,
              evidence.model ?? evidence.detector,
              evidence.confidence == null
                ? null
                : `${Math.round(evidence.confidence * 100)}%`,
              evidence.sceneTimestampSeconds == null
                ? null
                : `@ ${evidence.sceneTimestampSeconds.toFixed(2)}s`,
            ]
              .filter(Boolean)
              .join(" · "),
            color:
              evidence.matchStatus === "wrong_subject" ? "#f87171" : "#4ade80",
          });
        },
      );
    }
    if (visibleLayers.has("protected") && planEvidenceAllowed) {
      if (decision?.coordinateSpaceId === spaceId && decision.protectedRegion) {
        overlays.push({
          key: "protected-region",
          box: decision.protectedRegion,
          label: "protected region",
          color: "#facc15",
          dashed: true,
        });
      }
      spaceObservations
        .filter(
          (observation) =>
            observation.subjectId != null &&
            decision?.protectedSubjectIds.includes(observation.subjectId),
        )
        .forEach((observation, index) => {
          overlays.push({
            key: `protected-${observation.subjectId ?? "unassigned"}-${observation.frameIndex ?? "no-frame"}-${observation.timestampSeconds ?? "timeless"}-${index}`,
            box: observation.bbox,
            label: `protected · ${observation.label ?? observation.kind ?? "subject"}`,
            color: "#facc15",
            dashed: true,
          });
        });
    }
    if (
      visibleLayers.has("source_transform") &&
      (planEvidenceAllowed || appliedEvidenceAllowed)
    ) {
      coordinateSpaces
        .filter(
          (child) =>
            child.parentSpaceId === spaceId &&
            child.transformFromParent?.crop != null &&
            (child.evidenceRole !== "proposed_enforce" ||
              planEvidenceAllowed) &&
            (child.evidenceRole !== "actual_applied" || appliedEvidenceAllowed),
        )
        .forEach((child) => {
          overlays.push({
            key: `transform-${child.id}`,
            box: child.transformFromParent!.crop!,
            label: `${child.stage.replaceAll("_", " ")} source crop`,
            color: "#f43f5e",
            dashed: true,
          });
        });
    }
    if (
      visibleLayers.has("visible_content") &&
      appliedEvidenceAllowed &&
      coordinateSpace?.transformFromParent?.contentBox
    ) {
      overlays.push({
        key: "visible-content",
        box: coordinateSpace.transformFromParent.contentBox,
        label: "visible source pixels (padding outside)",
        color: "#38bdf8",
        dashed: true,
      });
    }
    if (
      visibleLayers.has("visible_content") &&
      (planEvidenceAllowed || appliedEvidenceAllowed)
    ) {
      const effect = appliedEvidenceAllowed
        ? (receipt?.effectViewport ?? decision?.effectViewport)
        : decision?.effectViewport;
      const deliveryContent =
        effect?.visibleContentBox ??
        (appliedEvidenceAllowed ? receipt?.visibleContentBox : null) ??
        decision?.visibleContentBox;
      const contentSpaceMatches =
        effect?.coordinateSpaceId === spaceId ||
        normalizedStage === "pre_effect_delivery";
      if (deliveryContent && contentSpaceMatches) {
        overlays.push({
          key: "delivery-visible-content",
          box: deliveryContent,
          label:
            (receipt?.strategy ?? decision?.strategy) === "postcard"
              ? "postcard source-photo bounds"
              : "delivery visible source pixels",
          color: "#38bdf8",
          dashed: true,
        });
      }
    }
    if (
      visibleLayers.has("planned_crop") &&
      planEvidenceAllowed &&
      decision?.coordinateSpaceId === spaceId
    ) {
      const crop =
        cropWindowAtTime(decision.cropPath, timeSeconds) ?? decision.cropWindow;
      if (crop) {
        overlays.push({
          key: "planned-crop",
          box: crop,
          label:
            coordinateSpace?.evidenceRole === "proposed_enforce"
              ? "proposed crop (not applied)"
              : "planned crop",
          color: "#fb923c",
          dashed: true,
        });
      }
    }
    if (visibleLayers.has("safe_zone") && planEvidenceAllowed) {
      for (const zone of decision?.safeZones ?? []) {
        if (zone.coordinateSpaceId !== spaceId) continue;
        overlays.push({
          key: `safe-zone-${zone.kind}-${zone.coordinateSpaceId}`,
          box: zone.box,
          label: `${zone.kind.replaceAll("_", " ")} safe zone`,
          color: "#a78bfa",
          dashed: true,
        });
      }
    }
    if (
      visibleLayers.has("applied_crop") &&
      appliedEvidenceAllowed &&
      receipt?.sourceCoordinateSpaceId === spaceId
    ) {
      const crop =
        cropWindowAtTime(receipt.cropPath, timeSeconds) ?? receipt.cropWindow;
      if (crop) {
        overlays.push({
          key: "applied-crop",
          box: crop,
          label: "applied crop",
          color: "#4ade80",
        });
      }
    }
    if (
      visibleLayers.has("effect_viewport") &&
      (planEvidenceAllowed || appliedEvidenceAllowed)
    ) {
      const effect = appliedEvidenceAllowed
        ? (receipt?.effectViewport ?? decision?.effectViewport)
        : decision?.effectViewport;
      const effectSpaceMatches =
        effect?.coordinateSpaceId === spaceId ||
        (effect?.coordinateSpaceId === "delivery_canvas" &&
          normalizedStage === "pre_effect_delivery");
      const effectWindow = effect
        ? (cropWindowAtTime(effect.cropPath, timeSeconds) ?? effect.cropWindow)
        : null;
      if (effectWindow && effectSpaceMatches) {
        overlays.push({
          key: "effect-viewport",
          box: effectWindow,
          label: `${effect?.effectType?.replaceAll("_", " ") ?? "effect"} viewport`,
          color: "#f472b6",
          dashed: true,
        });
      }
    }
    return overlays;
  }, [
    coordinateSpace,
    coordinateSpaces,
    decision,
    deliveredEvidence,
    evidenceMode,
    observations,
    receipt,
    timeSeconds,
    visibleLayers,
  ]);

  return (
    <svg
      ref={svgRef}
      className="pointer-events-none absolute inset-0 size-full"
      viewBox={`0 0 ${Math.max(size.width, 1)} ${Math.max(size.height, 1)}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {rects.map((overlay) => {
        const projected = projectNormalizedBox(overlay.box, mediaRect);
        return (
          <g key={overlay.key}>
            <rect
              x={projected.x}
              y={projected.y}
              width={projected.width}
              height={projected.height}
              fill="transparent"
              stroke={overlay.color}
              strokeWidth="2"
              strokeDasharray={overlay.dashed ? "7 4" : undefined}
              vectorEffect="non-scaling-stroke"
            />
            <rect
              x={projected.x}
              y={Math.max(0, projected.y - 19)}
              width={Math.min(
                Math.max(70, overlay.label.length * 6.4),
                Math.max(70, size.width - projected.x),
              )}
              height="19"
              fill="rgba(0,0,0,0.78)"
            />
            <text
              x={projected.x + 4}
              y={Math.max(14, projected.y - 5)}
              fill={overlay.color}
              fontSize="11"
              fontFamily="ui-monospace, monospace"
            >
              {overlay.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
